import { useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useQuery } from '@apollo/client';

import {
  GetIrlEventRewardByDispensaryDocument,
  GetIrlEventRewardDocument,
  IrlEventTrackingEvent,
} from 'src/gql/graphql';
import type { IrlEventReward } from 'src/gql/graphql';
import useUser from 'hooks/use-user';
import { useIrlEventTracking } from './use-irl-event-tracking';
import { useIrlEventParams } from './use-irl-event-params';
import type { LoyaltyUserType } from '../types';

type UseIrlEventRewardParams = {
  hasIncentive: boolean;
  hasLoyaltyProgram: boolean;
  isEligibleForReward: boolean;
  loyaltyUserType: LoyaltyUserType;
  optInRequired: boolean;
};

type BaseEventRewardReturn = {
  error: Error | null;
  loading: boolean;
};

type RewardNotShown = {
  reward: IrlEventReward | null;
  showReward: false;
};

type RewardShown = {
  reward: IrlEventReward;
  showReward: true;
};

type UseIrlEventRewardReturn = BaseEventRewardReturn & (RewardNotShown | RewardShown);

const REWARD_EVENT_MAP: Record<LoyaltyUserType, IrlEventTrackingEvent> = {
  existing: IrlEventTrackingEvent.rewardExistingLoyaltyUser,
  new: IrlEventTrackingEvent.rewardNewLoyaltyUser,
  none: IrlEventTrackingEvent.rewardNoLoyaltyUser,
};

const NO_REWARD_EVENT_MAP: Record<LoyaltyUserType, IrlEventTrackingEvent> = {
  existing: IrlEventTrackingEvent.noRewardExistingLoyaltyUser,
  new: IrlEventTrackingEvent.noRewardNewLoyaltyUser,
  none: IrlEventTrackingEvent.noRewardNoLoyaltyUser,
};

export type GetRewardTrackingEventParams = {
  hasIncentive: boolean;
  isEligibleForReward: boolean;
  loyaltyUserType: LoyaltyUserType;
  optInRequired: boolean;
};

export function getRewardTrackingEvent({
  hasIncentive,
  isEligibleForReward,
  loyaltyUserType,
  optInRequired,
}: GetRewardTrackingEventParams): IrlEventTrackingEvent | null {
  if (!hasIncentive) {
    // If enrollment was required and user is 'none', they skipped → skippedLoyalty already fired
    if (loyaltyUserType === 'none' && optInRequired) {
      return null;
    }
    return NO_REWARD_EVENT_MAP[loyaltyUserType];
  }

  const eventMap = isEligibleForReward ? REWARD_EVENT_MAP : NO_REWARD_EVENT_MAP;
  const event = eventMap[loyaltyUserType];

  // Exclude noRewardNoLoyaltyUser from incentive path (only valid via non-incentive path)
  if (event === IrlEventTrackingEvent.noRewardNoLoyaltyUser) {
    return null;
  }

  return event;
}

function computeShowReward(reward: IrlEventReward, isEligibleForReward: boolean, hasLoyaltyProgram: boolean): boolean {
  if (reward.rewardType === 'none') {
    return false;
  }
  const isDiscountReward = reward.rewardType === 'discount';
  const isLoyaltyReward = reward.rewardType === 'loyalty';
  return (isDiscountReward && (isEligibleForReward || !hasLoyaltyProgram)) || (isLoyaltyReward && isEligibleForReward);
}

export function useIrlEventReward({
  hasIncentive,
  hasLoyaltyProgram,
  isEligibleForReward,
  loyaltyUserType,
  optInRequired,
}: UseIrlEventRewardParams): UseIrlEventRewardReturn {
  const {
    irlEventId,
    enterpriseId,
    isoRegionCode,
    dispensaryId,
    hasEnterpriseParams,
    hasDispensaryParam,
  } = useIrlEventParams();
  const {
    trackEvent,
    called: trackingCalled,
    loading: trackingLoading,
    error: trackingError,
    success: trackingSuccess,
    completed: trackingCompleted,
  } = useIrlEventTracking();

  const user = useUser();
  const ecommUserId = useObserver(() => user.id ?? '');

  const hasRequiredParams = !!irlEventId && !!ecommUserId && (hasEnterpriseParams || hasDispensaryParam);

  /**
   * Determine which tracking event to fire before fetching the reward.
   * The tracking event grants the discount/point boost in LLx.
   */
  const trackingEvent = useMemo(
    () => getRewardTrackingEvent({ hasIncentive, isEligibleForReward, loyaltyUserType, optInRequired }),
    [hasIncentive, isEligibleForReward, loyaltyUserType, optInRequired]
  );

  useEffect(() => {
    if (!hasRequiredParams || trackingCalled || !trackingEvent) {
      return;
    }

    void trackEvent(trackingEvent);
  }, [hasRequiredParams, trackingCalled, trackEvent, trackingEvent]);

  const shouldFetchReward = hasRequiredParams && (!trackingEvent || (trackingCompleted && trackingSuccess));

  const {
    data: enterpriseRewardData,
    loading: enterpriseRewardLoading,
    error: enterpriseRewardError = null,
  } = useQuery(GetIrlEventRewardDocument, {
    variables: { irlEventId, ecommUserId, enterpriseId, isoRegionCode },
    skip: !shouldFetchReward || hasDispensaryParam,
  });

  const {
    data: dispensaryRewardData,
    loading: dispensaryRewardLoading,
    error: dispensaryRewardError = null,
  } = useQuery(GetIrlEventRewardByDispensaryDocument, {
    variables: { irlEventId, ecommUserId, dispensaryId },
    skip: !shouldFetchReward || !hasDispensaryParam,
  });

  const rewardData = hasDispensaryParam
    ? dispensaryRewardData?.getIrlEventRewardByDispensary
    : enterpriseRewardData?.getIrlEventReward;
  const rewardLoading = hasDispensaryParam ? dispensaryRewardLoading : enterpriseRewardLoading;
  const rewardError = hasDispensaryParam ? dispensaryRewardError : enterpriseRewardError;

  const reward = rewardData?.primary ?? null;
  const showReward = !!reward && computeShowReward(reward, isEligibleForReward, hasLoyaltyProgram);

  const loading =
    (hasRequiredParams && trackingEvent && (!trackingCompleted || trackingLoading)) ||
    (shouldFetchReward && rewardLoading);

  const error =
    hasRequiredParams && trackingEvent && trackingCompleted && !trackingSuccess
      ? trackingError ?? new Error('Failed to track reward redemption')
      : rewardError;

  return showReward ? { reward, showReward: true, loading, error } : { reward, showReward: false, loading, error };
}
